<script setup lang="ts">
import {
    computed,
    ref,
    onMounted,
    onBeforeUnmount,
    watchEffect,
    nextTick,
} from "vue";
import { storeToRefs } from "pinia";

import { CategorySet } from "~/js/interfaces/facets";
import { getGlobalTranslation } from "~/js/composables/translations";
import { useTeambuildingState } from "~/js/stores/teambuildings-tm";
import { getClasses } from "~/js/composables/classes";

import FieldsetFacet from "~/vue/_teammasters/molecules/fieldsets/FieldsetFacet.vue";
import ButtonTertiary from "~/vue/_teammasters/atoms/buttons/ButtonTertiary.vue";
import ButtonFilter from "~/vue/_teammasters/atoms/buttons/ButtonFilter.vue";
import ButtonIcon from "~/vue/_teammasters/atoms/buttons/ButtonIcon.vue";
import ButtonText from "~/vue/_teammasters/atoms/buttons/ButtonText.vue";
import TextH1 from "~/vue/_teammasters/atoms/texts/TextH1.vue";
import TextH4 from "~/vue/_teammasters/atoms/texts/TextH4.vue";

interface Props {
    labels: any;
    filterset: Array<CategorySet | null>;
    showFilters: boolean;
    utilities?: string | null;
}
/**
 * VARIABLES
 * **/
// props & emits
const props = withDefaults(defineProps<Props>(), { utilities: "" });
const emit = defineEmits(["search"]);

// store
const teambuildingStore = useTeambuildingState();
const { teambuildings, filters, pagination } = storeToRefs(teambuildingStore);

// variables
const showMobileFilter = ref(false);
const elFilters = ref<HTMLElement | null>(null);
const classes = ref({
    heading: {
        display: "flex justify-between items-center",
    },
    badges: {
        display: "flex flex-wrap",
        spacing: "gap-2 my-3",
    },
    divider: {
        color: "bg-neutral-400",
        size: "w-full h-px",
        spacing: "my-6",
    },
    filterset: {
        spacing: "space-y-8 px-6 pb-6 sm:p-0",
        overflow: "overflow-auto xl:overflow-visible",
        flex: "flex-1 grow",
    },
    wrapper: {
        _default: "w-full z-60",
        display: "flex flex-col sm:!block",
        position: "fixed sm:static inset-0",
        color: "bg-white",
    },
    filterHeader: {
        _default: "sm:hidden",
        flex: "flex items-center justify-between",
        spacing: "p-6",
    },
    filterFooter: {
        color: "bg-black",
    },
    landmark: {
        _default:
            "z-20 whitespace-nowrap rounded-lg shadow-md opacity-0 pointer-events-none",
        display: "block w-fit fixed -top-screen",
        color: "text-neutral-900 bg-yellow-200 #{props.get('theme') != 'dark' ? 'focus:ring-indigo' : 'focus:ring-yellow-200 focus:rin-offset-2' }",
        spacing: "py-2 px-4 mb-4",
        text: "font-primary font-semibold",
        focus: "focus:static focus:outline-none focus:ring-2 focus:opacity-100 focus:pointer-events-all",
    },
});

/**
 * METHODS
 **/
const onClear = () => {
    teambuildingStore.resetFilters();
};

const onRemoveFacet = (type, value) => {
    filters.value[type].values.splice(
        filters.value[type].values.indexOf(value),
        1
    );
    emit("search", false);
};

const onRemoveSearch = () => {
    filters.value.searchQuery = "";
    emit("search", false);
};

const getCategory = (facet: CategorySet | null, filter: number | string) => {
    if (!facet) {
        return;
    }

    const category = facet?.categories?.find((cat: any) => cat.id == filter);

    if (category) {
        return category;
    }
};

const setResultsString = (string: string) => {
    return string.replace("#", `${pagination.value?.foundResults ?? 0}`);
};

const onHandleResize = () => {
    if (showMobileFilter.value && window.innerWidth > 640) {
        showMobileFilter.value = false;
    }
};

const onHandleKeystroke = (event: any) => {
    if (!showMobileFilter.value) return;

    if (event.key == "Escape") {
        showMobileFilter.value = false;
    }
};

const getButtonFilter = (type: string | null | undefined) => {
    if (!type) {
        return [];
    }

    return filters.value[type]?.values ?? [];
};

const getFilter = () => {
    return props.filterset.filter(
        (set: CategorySet | null) => set?.isFacet ?? false
    );
};

/**
 * COMPUTED
 **/
const filterSetBasedOnShowFilters = computed(() => {
    // return all filters including categories and purposes if these filters are shown

    if (props.showFilters) {
        return props.filterset;
    }

    return props.filterset.filter((set: CategorySet | null) => {
        if (!set) return;

        return set?.type != "categories" && set?.type != "purposes";
    });
});

const filterCount = computed(() => {
    let results = 0;

    filterSetBasedOnShowFilters.value.forEach((facet: CategorySet | null) => {
        if (!facet) {
            return;
        }

        const filter = filters.value[facet?.type];
        if (filter && filter?.values?.length > 0) {
            results += filter.values.length;
        }
    });

    return results;
});

/**
 * WACHTERS
 **/
watchEffect(() => {
    if (showMobileFilter.value) {
        document.body.style.overflow = "hidden";

        nextTick(() => {
            elFilters.value?.querySelector("li")?.focus();
        });
    } else {
        document.body.style.overflow = "";
    }
});

/**
 * LIFECYCLES
 **/
onMounted(() => {
    window.addEventListener("resize", onHandleResize);
    document.addEventListener("keydown", onHandleKeystroke);
    onHandleResize(); // To check initial screen size
});

onBeforeUnmount(() => {
    window.removeEventListener("resize", onHandleResize);
});
</script>

<template>
    <!-- HEADING -->
    <div :class="[getClasses(classes, 'heading'), utilities]">
        <TextH4 v-if="teambuildings" utilities="block">
            {{ pagination?.foundResults ?? 0 }}
            {{ getGlobalTranslation("results") }}
        </TextH4>

        <ButtonTertiary
            :content="getGlobalTranslation('Filter')"
            @click="() => (showMobileFilter = !showMobileFilter)"
            icon="fas fa-filter"
            utilities="sm:hidden"
            aria-controls="teambuilding-filters"
        />
    </div>

    <!-- SELECTED FILTERS (BADGES) -->
    <div
        v-if="filterCount > 0 || filters.searchQuery"
        :class="getClasses(classes, 'badges')"
    >
        <!-- list out all the chosen filters -->
        <ButtonFilter
            v-if="filters.searchQuery"
            :text="filters.searchQuery"
            @click="onRemoveSearch()"
        />
        <template
            v-for="facet in filterSetBasedOnShowFilters"
            :key="facet?.type"
        >
            <ButtonFilter
                v-for="filter in getButtonFilter(facet?.type)"
                :key="filter"
                @click="onRemoveFacet(facet?.type, filter)"
                :text="getCategory(facet ?? null, filter)?.title ?? ''"
            />
        </template>
    </div>

    <!-- CLEAR FILTERS -->
    <ButtonText
        v-if="filterCount > 0"
        :content="getGlobalTranslation('Clear all filters')"
        @click="onClear"
    />

    <div :class="getClasses(classes, 'divider')" />

    <!-- LANDMARK: SKIP FACETS -->
    <a href="#teleport-teambuilding" :class="getClasses(classes, 'landmark')">
        {{ getGlobalTranslation("Skip filters") }}
    </a>

    <!-- FACETS -->
    <div id="desktop-filters" />
    <Teleport :to="showMobileFilter ? 'body' : '#desktop-filters'">
        <div
            :class="[
                getClasses(classes, 'wrapper'),
                !showMobileFilter ? 'hidden' : '',
            ]"
            ref="elFilters"
            id="teambuilding-filters"
        >
            <div :class="getClasses(classes, 'filterHeader')">
                <TextH1>{{ getGlobalTranslation("filter") }}</TextH1>

                <ButtonIcon
                    :content="getGlobalTranslation('Close')"
                    @click="showMobileFilter = false"
                    icon="fas fa-xmark"
                    theme="dark"
                />
            </div>

            <div :class="getClasses(classes, 'filterset')">
                <FieldsetFacet
                    v-for="facet in getFilter()"
                    :defaults="
                        facet?.type
                            ? filters[facet?.type]?.values ?? null
                            : null
                    "
                    :description="
                        facet?.type == 'priceRanges'
                            ? labels?.priceRangeDescription
                            : null
                    "
                    :key="facet?.type ?? 'key'"
                    :label="getGlobalTranslation(`filter_${facet?.type ?? ''}`)"
                    :options="facet?.categories ?? null"
                    :type="facet?.type"
                    @input="emit('search')"
                />
            </div>

            <div :class="getClasses(classes, 'filterFooter')">
                <ButtonTertiary
                    :content="
                        setResultsString(getGlobalTranslation('Show # results'))
                    "
                    @click="() => (showMobileFilter = !showMobileFilter)"
                    icon="fas fa-filter"
                    theme="dark"
                    utilities="py-4 sm:hidden"
                    variant="wide"
                />
            </div>
        </div>
    </Teleport>
</template>
